// @flow
import FileSaver from 'file-saver'
import type { Answer } from 'types/answer'
import type { AnswerSignature } from 'types/answer_signature'
import type { Message } from 'types/message'
import type { PdfDocumentApiResponse } from 'types/pdf-document-api-response'
import type { Response } from 'types/response'

import {
  makeDeleteRequest,
  makeGetDownloadRequest,
  makeGetRequest,
  makePatchRequest,
  makePostFileRequest,
  makePostRequest,
} from './fetchCalls'

const REACT_APP_API_URL = process.env.REACT_APP_API_URL ?? ''

export async function createPdfDocument(
  formData: FormData
): Promise<{ id: string }> {
  return await makePostFileRequest(
    `${REACT_APP_API_URL}/pdf-filler/pdf-documents/`,
    {
      body: formData,
    }
  )
}

export async function deletePdfDocument(documentId: string): Promise<any> {
  return await makeDeleteRequest(
    `${REACT_APP_API_URL}/pdf-filler/pdf-documents/${documentId}/`
  )
}

export async function getPdfDocument(
  documentId: string
): Promise<PdfDocumentApiResponse> {
  return await makeGetRequest(
    `${REACT_APP_API_URL}/pdf-filler/pdf-documents/${documentId}/`
  )
}

export async function getPdfDocuments(): Promise<any> {
  return await makeGetRequest(`${REACT_APP_API_URL}/pdf-filler/pdf-documents/`)
}

export async function updatePdfDocument(
  documentId: string,
  data: { name: string }
): Promise<any> {
  return await makePatchRequest(
    `${REACT_APP_API_URL}/pdf-filler/pdf-documents/${documentId}/`,
    {
      body: JSON.stringify(data),
    }
  )
}

export async function createMessage(data: {
  message_type: string,
  team: number,
  document: string,
}): Promise<Message> {
  return await makePostRequest(`${REACT_APP_API_URL}/pdf-filler/messages/`, {
    body: JSON.stringify(data),
  })
}

export async function createAnswers(
  data: {
    answers: Array<Answer>,
    answer_signatures: Array<AnswerSignature>,
    submit: ?boolean,
  },
  mode: string
): Promise<Response> {
  return await makePostRequest(
    `${REACT_APP_API_URL}/pdf-filler/answers/?mode=${mode}`,
    {
      body: JSON.stringify(data),
    }
  )
}

export async function getResponse(responseId: string): Promise<Response> {
  return await makeGetRequest(
    `${REACT_APP_API_URL}/pdf-filler/responses/${responseId}/`
  )
}

export async function submitResponse(
  responseId: string,
  mode: string
): Promise<Response> {
  return await makePatchRequest(
    `${REACT_APP_API_URL}/pdf-filler/responses/${responseId}/?mode=${mode}`,
    {
      body: JSON.stringify({ is_submitted: true }),
    }
  )
}

export async function downloadPdfDocument(
  responseId: string,
  blankDocument: boolean = false,
  messageCode: ?string = undefined
) {
  const [filename, blob] = await makeGetDownloadRequest(
    `${REACT_APP_API_URL}/pdf-filler${
      blankDocument ? '/blank' : ''
    }/${responseId}/download/${
      messageCode ? `?message_code=${messageCode}` : ''
    }`
  )

  FileSaver.saveAs(blob, decodeURIComponent(filename))
}

export async function downloadBlankPdfDocument(
  documentId: string
): Promise<void> {
  return await downloadPdfDocument(documentId, true)
}

export async function updateMessage(
  messageId: string,
  data: {
    is_waiting: boolean,
  }
): Promise<Message> {
  return await makePatchRequest(
    `${REACT_APP_API_URL}/pdf-filler/messages/${messageId}/`,
    {
      body: JSON.stringify(data),
    }
  )
}

export async function enableDisablePdfDocument(
  uid: any,
  enabled: any
): Promise<any> {
  return await makePostRequest(
    `${REACT_APP_API_URL}/pdf-filler/pdf-documents/enable-disable/`,
    {
      body: JSON.stringify({ uid, enabled }),
    }
  )
}
